import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    setTimeout(() => {
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
      setSending(false);
    }, 1500);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-black relative">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#667eea]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 max-w-[1400px] relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Connect
          </p>
          <h2 className="font-syne text-4xl md:text-6xl font-bold tracking-tight text-white">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="text-base md:text-lg font-light tracking-wide text-zinc-400 mt-6 max-w-[600px]">
            We'd love to hear from you. Let's discuss your project.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-8"
            data-testid="contact-form"
          >
            <div>
              <input
                type="text"
                placeholder="Your Name"
                aria-label="Your Name"
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-white/10 focus:border-[#667eea] focus:ring-0 text-white text-lg py-4 px-0 placeholder:text-zinc-600 font-light transition-colors outline-none"
              />
            </div>
            <div>
              <input
                type="email"
                placeholder="Your Email"
                aria-label="Your Email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-white/10 focus:border-[#667eea] focus:ring-0 text-white text-lg py-4 px-0 placeholder:text-zinc-600 font-light transition-colors outline-none"
              />
            </div>
            <div>
              <textarea
                rows={5}
                placeholder="Tell us about your business..."
                aria-label="Your Message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-0 border-b border-white/10 focus:border-[#667eea] focus:ring-0 text-white text-lg py-4 px-0 placeholder:text-zinc-600 font-light transition-colors resize-none outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white font-bold rounded-full px-8 py-4 text-lg transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? "Sending..." : "Send Message"}
              <Send size={18} />
            </button>
          </motion.form>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-10"
          >
            <div className="flex items-start gap-5" data-testid="contact-email-info">
              <div className="w-12 h-12 flex items-center justify-center border border-white/10">
                <Mail size={18} className="text-zinc-500" />
              </div>
              <div>
                <h4 className="font-syne text-sm font-bold text-white mb-1">Email</h4>
                <a href="mailto:Info@digistromer.com" className="text-zinc-400 hover:text-[#ff6b6b] transition-colors font-light">
                  Info@digistromer.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-5" data-testid="contact-phone-info">
              <div className="w-12 h-12 flex items-center justify-center border border-white/10">
                <Phone size={18} className="text-zinc-500" />
              </div>
              <div>
                <h4 className="font-syne text-sm font-bold text-white mb-1">Phone</h4>
                <a href="tel:+971506485900" className="text-zinc-400 hover:text-[#ff6b6b] transition-colors font-light">
                  +971 50 648 5900
                </a>
              </div>
            </div>

            <div className="flex items-start gap-5" data-testid="contact-location-info">
              <div className="w-12 h-12 flex items-center justify-center border border-white/10">
                <MapPin size={18} className="text-zinc-500" />
              </div>
              <div>
                <h4 className="font-syne text-sm font-bold text-white mb-1">Location</h4>
                <p className="text-zinc-400 font-light">
                  3101, Vision Tower, Business Bay,<br />Dubai, UAE
                </p>
              </div>
            </div>

            <div className="pt-8 border-t border-white/5">
              <h4 className="font-syne text-sm font-bold text-white mb-4">Follow Us</h4>
              <div className="flex gap-6" data-testid="social-links">
                {["Instagram", "X (Twitter)", "LinkedIn", "Dribbble"].map((s) => (
                  <a
                    key={s}
                    href="#"
                    className="text-sm text-zinc-500 hover:text-[#ff6b6b] transition-colors font-medium"
                  >
                    {s}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
